import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLoaderData } from "react-router";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarDays, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import type { PriceTier, RetreatDetail, RetreatDetailResponse, RetreatWaitlist } from "@/lib/retreats";
import { Blue2Page, Part } from "../Page";
import { Draw, EASE, HEART, Head, Ink, PAGES, VIEW } from "../bits";
import "./Retreat.css";

/* One retreat, in the blue-2 style. Everything shown comes from the retreat
   detail API (same loader as /retreats/:slug). Booking, coupons, the waitlist
   and Stripe checkout behave exactly as on the existing detail page; their
   logic is mirrored from RetreatDetail.tsx (keep the two in sync). Stripe
   returns buyers to the existing /retreats/success and /retreats/cancel. */

// Fields the API returns that the shared type doesn't list yet.
type Retreat = RetreatDetail & { cancellationPolicyMd?: string | null };

/* Retreat dates are stored as calendar days, so format them in UTC. */
const fmt = (iso: string, o: Intl.DateTimeFormatOptions) => new Date(iso).toLocaleDateString("en-US", { timeZone: "UTC", ...o });

function dateRange(start: string, end: string) {
  const sameMonth = fmt(start, { month: "long", year: "numeric" }) === fmt(end, { month: "long", year: "numeric" });
  return sameMonth
    ? `${fmt(start, { month: "long" })} ${fmt(start, { day: "numeric" })}–${fmt(end, { day: "numeric" })}, ${fmt(end, { year: "numeric" })}`
    : `${fmt(start, { month: "long", day: "numeric" })} – ${fmt(end, { month: "long", day: "numeric" })}, ${fmt(end, { year: "numeric" })}`;
}

function shortRange(start: string, end: string) {
  return fmt(start, { month: "short" }) === fmt(end, { month: "short" })
    ? `${fmt(start, { month: "short" })} ${fmt(start, { day: "numeric" })}–${fmt(end, { day: "numeric" })}`
    : `${fmt(start, { month: "short", day: "numeric" })} – ${fmt(end, { month: "short", day: "numeric" })}`;
}

const formatPrice = (cents: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
const money = (cents: number) => `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: cents % 100 ? 2 : 0 })}`;
/** Leading emoji in admin-entered labels ("🧥 Quilted Vest Project") reads as clutter in this design. */
const plain = (s: string) => s.replace(/^[\p{Extended_Pictographic}️‍\s]+/u, "");
const city = (location: string) => location.split(",")[0].trim();

const TICK = "M7 13 L11.5 17.5 L19.5 8";

/* ─── Header (gold) ─── */

function Header({ r, waitlist }: { r: Retreat; waitlist: RetreatWaitlist | null }) {
  const isOpen = r.status === "open", sold = r.status === "sold-out";
  const place = city(r.location);
  return <div className="brd-head">
    <div className="brd-head-copy">
      <Link to={PAGES.retreats} className="brd-back"><ArrowLeft size={15} aria-hidden="true" /> All retreats</Link>
      <p className="brd-kicker">
        <span className={`brd-status brd-status-${r.status}`}>{isOpen ? "Booking open" : sold ? "Sold out" : r.status === "past" ? "Past retreat" : "Coming soon"}</span>
        {r.subtitle && <span>{r.subtitle}</span>}
      </p>
      <h1 className="brd-title" aria-label={r.title}>
        <motion.span className="brd-title-hand" aria-hidden="true" initial={{ opacity: 0, rotate: -12 }} animate={{ opacity: 1, rotate: -5 }} transition={{ delay: 0.4, duration: 0.5, ease: EASE }}>
          {r.status === "past" ? "we sewed in" : "sew with me in"}
        </motion.span>
        <span className={`brd-title-word${place.length > 9 ? " is-long" : ""}`} aria-hidden="true">
          {place.split("").map((c, i) => <motion.span key={i} initial={{ y: "18%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} transition={{ delay: 0.1 + i * 0.045, duration: 0.6, ease: EASE }}>{c === " " ? " " : c}</motion.span>)}
        </span>
      </h1>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.5, ease: EASE }}>
        <ul className="brd-facts">
          <li><CalendarDays size={18} aria-hidden="true" />{dateRange(r.startDate, r.endDate)}</li>
          <li><MapPin size={18} aria-hidden="true" />{[r.venue, r.location].filter(Boolean).join(", ")}</li>
        </ul>
        {r.tagline && <p className="brd-lede">{r.tagline}</p>}
        <div className="brd-actions">
          {isOpen && r.priceTiers.length > 0 && <a href="#reserve" className="b2-btn brd-btn-ink">Reserve your spot <ArrowRight size={17} aria-hidden="true" /></a>}
          {waitlist && <a href="#waitlist" className="b2-btn b2-btn-ghost">{sold ? "Join the waitlist" : "Join the waitlist"}</a>}
          {!isOpen && !waitlist && <Link to={PAGES.retreats} className="b2-btn brd-btn-ink">See upcoming retreats <ArrowRight size={17} aria-hidden="true" /></Link>}
        </div>
      </motion.div>
    </div>
    <motion.div className="brd-head-art" initial={{ opacity: 0, y: 40, rotate: 8 }} animate={{ opacity: 1, y: 0, rotate: 3 }} transition={{ delay: 0.35, type: "spring", stiffness: 130, damping: 15 }}>
      <i className="brd-tape" aria-hidden="true" />
      {r.heroImageUrl
        ? <div className="brd-cover brd-cover-photo"><img src={r.heroImageUrl} alt="" /></div>
        // TODO(content): no hero photo for this retreat yet; the gingham swatch stands in.
        : <div className="brd-cover" aria-hidden="true">
          <span className="brd-cover-year">{fmt(r.endDate, { year: "numeric" })}</span>
          <span className="brd-cover-place">{place}</span>
          <span className="brd-cover-dates">{shortRange(r.startDate, r.endDate)}</span>
          <svg className="brd-cover-heart" viewBox="0 0 30 46"><Draw d={HEART} delay={1} width={3.2} /></svg>
        </div>}
      <p className="brd-head-cap">{r.venue ?? r.location} <b aria-hidden="true">♥</b></p>
    </motion.div>
  </div>;
}

/* ─── Waitlist (mirrors WaitlistForm in RetreatDetail.tsx) ─── */

function WaitlistForm({ retreat, waitlist }: { retreat: Retreat; waitlist: RetreatWaitlist }) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setStatus("idle");
    setMessage(null);
    try {
      await api.post(`/api/retreats/${retreat.slug}/waitlist`, { email, firstName: firstName || undefined });
      setStatus("success");
      setMessage("You are on this retreat waitlist.");
      setFirstName("");
      setEmail("");
    } catch (e) {
      setStatus("error");
      setMessage(e instanceof Error ? e.message : "Waitlist signup failed");
    } finally {
      setBusy(false);
    }
  }

  return <form id="waitlist" className="brd-waitlist" onSubmit={submit}>
    <i className="brd-tape" aria-hidden="true" />
    <p className="brd-label">{waitlist.name}</p>
    <h2>Get first notice when spots open.</h2>
    <p className="brd-waitlist-note">This list is only for {retreat.title}. You will not be mixed into a different retreat waitlist.</p>
    <div className="brd-fields">
      <label><span>First name</span><input name="firstName" autoComplete="given-name" value={firstName} onChange={e => setFirstName(e.target.value)} /></label>
      <label><span>Email address</span><input name="email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} /></label>
    </div>
    {message && <p className={`brd-msg ${status === "success" ? "is-ok" : "is-err"}`} role={status === "error" ? "alert" : "status"}>{message}</p>}
    <button type="submit" className="b2-btn brd-btn-solid" disabled={busy}>{busy ? "Joining..." : "Join waitlist"}</button>
  </form>;
}

function WaitlistBand({ retreat, waitlist }: { retreat: Retreat; waitlist: RetreatWaitlist }) {
  return <Part p="words">
    <section className="b2-sec brd-wl" aria-label="Retreat waitlist">
      <div className="b2-wrap brd-wl-grid">
        <div>
          <p className="b2-note brd-wl-note">{retreat.status === "sold-out" ? "every seat is spoken for…" : "not ready to book?"}</p>
          <p className="brd-wl-big">{retreat.status === "sold-out" ? "but spots do open up." : "hop on the list."}</p>
        </div>
        <WaitlistForm retreat={retreat} waitlist={waitlist} />
      </div>
    </section>
  </Part>;
}

/* ─── About + quick details, with a sticky booking card ─── */

function fromPrice(tiers: PriceTier[]) {
  const full = tiers.filter(t => t.tierType === "full").map(t => t.priceCents);
  const deposit = tiers.find(t => t.tierType === "deposit");
  return { full: full.length ? Math.min(...full) : null, deposit: deposit?.priceCents ?? null };
}

function BookingCard({ r, waitlist }: { r: Retreat; waitlist: RetreatWaitlist | null }) {
  const isOpen = r.status === "open";
  const { full, deposit } = fromPrice(r.priceTiers);
  return <aside className="brd-book" aria-label="Book this retreat">
    <i className="brd-tape" aria-hidden="true" />
    <p className="brd-label">{r.title}</p>
    <p className="brd-book-dates">{dateRange(r.startDate, r.endDate)}</p>
    <p className="brd-book-where">{[r.venue, r.location].filter(Boolean).join(" · ")}</p>
    {isOpen && full !== null && <p className="brd-book-price"><small>from</small><b>{money(full)}</b>{deposit !== null && <span>or hold your spot with a {money(deposit)} deposit</span>}</p>}
    {isOpen && r.priceTiers.length > 0 && <a href="#reserve" className="b2-btn brd-btn-solid">Build your retreat <ArrowRight size={17} aria-hidden="true" /></a>}
    {r.status === "sold-out" && <p className="brd-book-sold">Sold out</p>}
    {waitlist && <a href="#waitlist" className="brd-textlink">Join the waitlist</a>}
  </aside>;
}

function About({ r, waitlist }: { r: Retreat; waitlist: RetreatWaitlist | null }) {
  const paras = (r.aboutBodyMd ?? "").split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
  if (!paras.length && !r.details.length) return null;
  return <Part p="making">
    <section className="b2-sec brd-about" aria-labelledby="brd-about-title">
      <div className="b2-wrap brd-about-grid">
        <div>
          <Head id="brd-about-title" hand="about this" word="retreat" tone="blue" className="brd-head-sm" />
          {paras.length > 0 && <div className="brd-about-body">
            {paras.map((p, i) => <p key={i} className={i === 0 ? "brd-about-first" : undefined}>{p}</p>)}
          </div>}
          {r.details.length > 0 && <>
            <p className="b2-note brd-details-note">quick details</p>
            <ul className="brd-details">
              {r.details.map(d => {
                const [k, ...v] = plain(d.label).split(":");
                return <li key={d.label}>{v.length ? <><small>{k}</small><span>{v.join(":").trim()}</span></> : <span>{k}</span>}</li>;
              })}
            </ul>
          </>}
        </div>
        <BookingCard r={r} waitlist={waitlist} />
      </div>
    </section>
  </Part>;
}

/* ─── Project + what's included (teal) ─── */

function Included({ r }: { r: Retreat }) {
  if (!r.included.length && !r.projects.length) return null;
  return <Part p="club">
    <section id="included" className="b2-sec brd-incl" aria-labelledby="brd-incl-title">
      <div className="b2-wrap brd-incl-grid">
        <div>
          <Head id="brd-incl-title" hand="what’s" word="included" tone="pink" className="brd-head-sm" />
          {r.included.length > 0 && <motion.ul className="brd-checks" initial="off" whileInView="on" viewport={VIEW}>
            {r.included.map((x, i) => <li key={x.label}>
              <svg viewBox="0 0 26 26" aria-hidden="true"><rect x="2" y="2" width="22" height="22" rx="4" fill="none" stroke="currentColor" strokeWidth="2.4" />
                <motion.path d={TICK} fill="none" stroke="currentColor" strokeWidth={2.8} strokeLinecap="round" strokeLinejoin="round"
                  variants={{ off: { pathLength: 0, opacity: 0 }, on: { pathLength: 1, opacity: 1, transition: { delay: 0.3 + i * 0.15, duration: 0.4, opacity: { delay: 0.3 + i * 0.15, duration: 0.01 } } } }} /></svg>
              <span>{plain(x.label)}</span>
            </li>)}
          </motion.ul>}
        </div>
        <div className="brd-projects">
          {r.projects.map((p, i) => <motion.article key={p.name} className="brd-project" aria-labelledby={`brd-project-${i}`}
            initial={{ opacity: 0, y: 40, rotate: 4 }} whileInView={{ opacity: 1, y: 0, rotate: i % 2 ? -1.5 : 1.5 }} viewport={VIEW} transition={{ type: "spring", stiffness: 110, damping: 16 }}>
            <i className="brd-tape" aria-hidden="true" />
            <p className="brd-label">{r.projects.length > 1 ? `Project ${i + 1}` : "The project"}</p>
            <h3 id={`brd-project-${i}`}>{plain(p.name)}</h3>
            {p.description && <p className="brd-project-desc">{p.description}</p>}
            {p.kitContents && p.kitContents.length > 0 && <>
              <p className="brd-project-label">You’ll get</p>
              <ul>{p.kitContents.map(k => <li key={k.item}>{k.item}</li>)}</ul>
            </>}
          </motion.article>)}
        </div>
      </div>
    </section>
  </Part>;
}

/* ─── Schedule (only when the retreat has one) ─── */

function Schedule({ r }: { r: Retreat }) {
  // TODO(content): the April 2027 retreat has no schedule yet; this section appears once one is added in admin.
  if (!r.schedule.length) return null;
  return <Part p="brands">
    <section className="b2-sec brd-sched" aria-labelledby="brd-sched-title">
      <div className="b2-wrap">
        <Head id="brd-sched-title" hand="how the days" word="go" tone="blue" className="brd-head-sm" />
        <ol className="brd-days">
          {r.schedule.map((d, i) => <motion.li key={d.day + i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0, rotate: [-1.5, 1, -0.5, 1.5][i % 4] }} viewport={VIEW} transition={{ delay: i * 0.08, duration: 0.5, ease: EASE }}>
            <p className="brd-day">{d.day}</p>
            {d.label && <p className="brd-day-label">{d.label}</p>}
            {d.activities && d.activities.length > 0 && <ul>{d.activities.map((a, j) => <li key={j}>{a.time && <b>{a.time}</b>}<span>{a.activity}</span></li>)}</ul>}
          </motion.li>)}
        </ol>
      </div>
    </section>
  </Part>;
}

/* ─── Build your retreat (gold) — logic mirrors PricingBuilder in RetreatDetail.tsx ─── */

type Applied = {
  code: string;
  type: "percentage" | "fixed" | "final_price";
  value: number;
  appliesToTierType: "any" | "deposit" | "full" | "addon";
  description: string;
};

function PricingBuilder({ retreat }: { retreat: Retreat }) {
  const tiers = retreat.priceTiers;
  const rateOptions = tiers.map((t, i) => ({ t, i })).filter(({ t }) => t.tierType === "full" || t.tierType === "deposit");
  const addonOption = tiers.map((t, i) => ({ t, i })).find(({ t }) => t.tierType === "addon") ?? null;

  const [selected, setSelectedRaw] = useState(rateOptions[0]?.i ?? 0);
  const [vip, setVip] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState<Applied | null>(null);
  const [couponMsg, setCouponMsg] = useState<string | null>(null);
  const [couponBusy, setCouponBusy] = useState(false);

  const selectedTier = tiers[selected];
  const isDeposit = selectedTier?.tierType === "deposit";
  const effectiveVip = !isDeposit && vip && !!addonOption;
  const base = selectedTier?.priceCents ?? 0;
  const addonPrice = effectiveVip && addonOption ? addonOption.t.priceCents : 0;
  const discountTarget = applied?.appliesToTierType === "addon" ? addonPrice : base;
  const discount = applied
    ? applied.type === "percentage"
      ? Math.round(discountTarget * (applied.value / 100))
      : applied.type === "final_price"
        ? Math.max(0, discountTarget - applied.value)
        : Math.min(applied.value, discountTarget)
    : 0;
  const total = Math.max(0, base + addonPrice - discount);

  // switching rates can invalidate a tier-scoped coupon — clear it
  function selectRate(i: number) {
    setSelectedRaw(i);
    setApplied(null);
    setCouponMsg(null);
  }

  // removing the VIP upgrade invalidates an addon-scoped coupon
  function toggleVip() {
    const next = !vip;
    setVip(next);
    if (!next && applied?.appliesToTierType === "addon") {
      setApplied(null);
      setCouponMsg(null);
    }
  }

  async function applyCoupon() {
    const code = coupon.trim();
    if (!code) return;
    setCouponBusy(true);
    setCouponMsg(null);
    try {
      const r = await api.post<Applied>("/api/coupons/validate", {
        code,
        scope: "retreats",
        retreatId: retreat.id,
        tierTypes: [...(selectedTier ? [selectedTier.tierType] : []), ...(effectiveVip ? ["addon"] : [])],
        subtotalCents: base + addonPrice,
      });
      setApplied({ code: r.code, type: r.type, value: r.value, appliesToTierType: r.appliesToTierType, description: r.description });
    } catch (e) {
      setApplied(null);
      setCouponMsg(e instanceof Error ? e.message : "Invalid code");
    } finally {
      setCouponBusy(false);
    }
  }

  function tierSub(t: PriceTier): string {
    if (t.tierType === "deposit") return t.balanceDueCents ? `then ${formatPrice(t.balanceDueCents)} balance` : "reserve your spot";
    const m = t.description?.match(/save \$[\d,]+/i);
    return m ? m[0] : "standard rate";
  }

  async function reserve() {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post<{ url: string }>("/api/retreats/checkout", {
        retreatId: retreat.id,
        tierIndex: selected,
        ...(effectiveVip && addonOption ? { addonTierIndex: addonOption.i } : {}),
        ...(applied ? { couponCode: applied.code } : {}),
      });
      window.location.href = r.url;
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Checkout failed");
      setBusy(false);
    }
  }

  return <div className="brd-builder">
    <i className="brd-tape" aria-hidden="true" />
    <fieldset className="brd-step">
      <legend><span>1</span>Choose your rate</legend>
      <div className="brd-rates" role="radiogroup" aria-label="Rate">
        {rateOptions.map(({ t, i }) => <button key={i} type="button" role="radio" aria-checked={selected === i}
          className={`brd-rate${selected === i ? " is-on" : ""}`} onClick={() => selectRate(i)}>
          <small>{t.name}</small>
          <b>{formatPrice(t.priceCents)}</b>
          <span>{tierSub(t)}</span>
        </button>)}
      </div>
    </fieldset>

    {addonOption && !isDeposit && <fieldset className="brd-step">
      <legend><span>2</span>Optional upgrade</legend>
      <button type="button" role="checkbox" aria-checked={vip} className={`brd-vip${vip ? " is-on" : ""}`} onClick={toggleVip}>
        <i aria-hidden="true">{vip ? "✓" : ""}</i>
        <span className="brd-vip-copy">
          <span className="brd-vip-top"><b>{addonOption.t.name}</b><em>+{formatPrice(addonOption.t.priceCents)}</em></span>
          {addonOption.t.description && <span className="brd-vip-desc">{addonOption.t.description}</span>}
        </span>
      </button>
    </fieldset>}

    <div className="brd-step">
      <p className="brd-step-label">Discount code</p>
      {applied
        ? <div className="brd-coupon-on">
          <span>✓ {applied.code} — {applied.description}</span>
          <button type="button" onClick={() => { setApplied(null); setCoupon(""); setCouponMsg(null); }}>Remove</button>
        </div>
        : <div className="brd-coupon">
          <label className="brd-sr" htmlFor="brd-coupon">Discount code</label>
          <input id="brd-coupon" value={coupon} onChange={e => setCoupon(e.target.value)} placeholder="Enter code" />
          <button type="button" onClick={applyCoupon} disabled={couponBusy || !coupon.trim()}>{couponBusy ? "…" : "Apply"}</button>
        </div>}
      {couponMsg && <p className="brd-msg is-err" role="alert">{couponMsg}</p>}
    </div>

    <div className="brd-total">
      {discount > 0 && <p className="brd-discount"><span>Discount · {applied?.code}</span><span>−{formatPrice(discount)}</span></p>}
      <p className="brd-total-row"><span>Your total</span><b>{formatPrice(total)}</b></p>
      {err && <p className="brd-msg is-err" role="alert">{err}</p>}
      <button type="button" className="brd-reserve" onClick={reserve} disabled={busy}>
        {busy ? "Redirecting to Stripe…" : `Reserve at ${formatPrice(total)}`} {!busy && <ArrowRight size={18} aria-hidden="true" />}
      </button>
      {isDeposit && selectedTier?.balanceDueCents
        ? <p className="brd-total-note">Pay {formatPrice(base)} now to hold your spot; {formatPrice(selectedTier.balanceDueCents)} balance due before the retreat.</p>
        : <p className="brd-total-note">Secure checkout with Stripe. You’ll add your name and email there.</p>}
    </div>
  </div>;
}

function Build({ r }: { r: Retreat }) {
  return <Part p="loop">
    <section id="reserve" className="b2-sec brd-build" aria-labelledby="brd-build-title">
      <div className="b2-wrap brd-build-grid">
        <div className="brd-build-side">
          <Head id="brd-build-title" hand="build your" word="retreat" tone="blue" className="brd-head-sm" />
          <p className="b2-lede">Pick your rate, add the optional VIP upgrade, and see your total update live.</p>
          <p className="b2-note brd-build-note">can’t wait to sew with you!
            <svg viewBox="0 0 30 46" aria-hidden="true"><Ink d={HEART} delay={0.6} width={3.2} /></svg>
          </p>
        </div>
        <PricingBuilder retreat={r} />
      </div>
    </section>
  </Part>;
}

/* ─── Good to know: travel, policies, FAQ, sponsors ─── */

function GoodToKnow({ r }: { r: Retreat }) {
  const [open, setOpen] = useState(0);
  if (!r.faq.length && !r.travelInfo && !r.sponsors.length && !r.cancellationPolicyMd) return null;
  return <Part p="faves">
    <section className="b2-sec brd-know" aria-labelledby="brd-know-title">
      <div className="b2-wrap brd-know-grid">
        <div>
          <Head id="brd-know-title" hand="good to" word="know" tone="blue" className="brd-head-sm" />
          {r.travelInfo && <div className="brd-note-card">
            <p className="brd-label">Getting there</p>
            <p>{r.travelInfo}</p>
          </div>}
          {r.cancellationPolicyMd && <div className="brd-note-card">
            <p className="brd-label">Cancellation policy</p>
            <p>{r.cancellationPolicyMd}</p>
          </div>}
        </div>
        {r.faq.length > 0 && <div className="brd-faq">
          <p className="b2-note brd-faq-note">a few things you might wonder</p>
          {r.faq.map((f, i) => <details key={f.question} open={i === open} onToggle={e => { if ((e.currentTarget as HTMLDetailsElement).open) setOpen(i); }}>
            <summary>{f.question}<span aria-hidden="true" /></summary>
            <p>{f.answer}</p>
          </details>)}
        </div>}
      </div>
      {r.sponsors.length > 0 && <div className="b2-wrap brd-sponsors">
        <p className="b2-note">sponsored by</p>
        <ul>{r.sponsors.map(s => <li key={s.name}>
          {s.websiteUrl
            ? <a href={s.websiteUrl} target="_blank" rel="noreferrer">{s.logoUrl ? <img src={s.logoUrl} alt={s.name} /> : s.name}</a>
            : s.logoUrl ? <img src={s.logoUrl} alt={s.name} /> : s.name}
        </li>)}</ul>
      </div>}
    </section>
  </Part>;
}

/* ─── Phone: a booking bar that stays in reach until the builder is on screen ─── */

function StickyBook({ r }: { r: Retreat }) {
  const [show, setShow] = useState(false);
  const builderSeen = useRef(false);
  useEffect(() => {
    const target = document.getElementById("reserve");
    const io = target && new IntersectionObserver(([e]) => { builderSeen.current = e.isIntersecting; setShow(window.scrollY > 500 && !e.isIntersecting); });
    if (target && io) io.observe(target);
    const onScroll = () => setShow(window.scrollY > 500 && !builderSeen.current);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { io?.disconnect(); window.removeEventListener("scroll", onScroll); };
  }, []);
  const { full } = fromPrice(r.priceTiers);
  return <div className={`brd-sticky${show ? " is-on" : ""}`} aria-hidden={!show}>
    <span><small>{shortRange(r.startDate, r.endDate)} · {city(r.location)}</small>{full !== null && <b>from {money(full)}</b>}</span>
    <a href="#reserve" className="brd-sticky-btn" tabIndex={show ? 0 : -1}>Reserve <ArrowRight size={16} aria-hidden="true" /></a>
  </div>;
}

export function Blue2Retreat() {
  const { retreat, waitlist } = useLoaderData() as RetreatDetailResponse;
  const r = retreat as Retreat;
  const canBook = r.status === "open" && r.priceTiers.length > 0;
  return <Blue2Page current="retreats" tone="gold" header={<Header r={r} waitlist={waitlist} />}>
    {waitlist && <WaitlistBand retreat={r} waitlist={waitlist} />}
    <About r={r} waitlist={waitlist} />
    <Included r={r} />
    <Schedule r={r} />
    {canBook && <Build r={r} />}
    <GoodToKnow r={r} />
    {canBook && <StickyBook r={r} />}
  </Blue2Page>;
}
